export type RosterSizeColumn = {
    name: string;
    key: string;
    header: string;
    label: string;
};

const SIZE_DEFS: { [name: string]: RosterSizeColumn } = {
    XSmall: { name: 'XSmall', key: 'xSmall', header: 'xs', label: 'XSmall' },
    Small: { name: 'Small', key: 'small', header: 'sm', label: 'Small' },
    Medium: { name: 'Medium', key: 'medium', header: 'md', label: 'Medium' },
    Large: { name: 'Large', key: 'large', header: 'lg', label: 'Large' },
    XLarge: { name: 'XLarge', key: 'xLarge', header: 'xl', label: 'XLarge' },
    XXL: { name: 'XXL', key: 'xxl', header: 'xxl', label: 'XXL' },
    XXXL: { name: 'XXXL', key: 'xxxl', header: 'xxxl', label: 'XXXL' },
    XXXXL: { name: 'XXXXL', key: 'xxxxl', header: '4xl', label: '4XL' },
    XXXXXL: { name: 'XXXXXL', key: 'xxxxxl', header: '5xl', label: '5XL' },
    XXXXXXL: { name: 'XXXXXXL', key: 'xxxxxxl', header: '6xl', label: '6XL' },
    YXSmall: { name: 'YXSmall', key: 'yxSmall', header: 'yxs', label: 'YXSmall' },
    YSmall: { name: 'YSmall', key: 'ySmall', header: 'ysm', label: 'YSmall' },
    YMedium: { name: 'YMedium', key: 'yMedium', header: 'ymd', label: 'YMedium' },
    YLarge: { name: 'YLarge', key: 'yLarge', header: 'ylg', label: 'YLarge' },
    YXLarge: { name: 'YXLarge', key: 'yxLarge', header: 'yxl', label: 'YXLarge' },
    Infant6: { name: 'Infant6', key: 'infant6', header: '6mo', label: '6mos' },
    Infant12: { name: 'Infant12', key: 'infant12', header: '12mo', label: '12mos' },
    Infant18: { name: 'Infant18', key: 'infant18', header: '18mo', label: '18mos' },
    Infant24: { name: 'Infant24', key: 'infant24', header: '24mo', label: '24mos' },
    Toddler2: { name: 'Toddler2', key: 'toddler2', header: '2t', label: '2T' },
    Toddler3: { name: 'Toddler3', key: 'toddler3', header: '3t', label: '3T' },
    Toddler4: { name: 'Toddler4', key: 'toddler4', header: '4t', label: '4T' },
    Toddler5: { name: 'Toddler5', key: 'toddler5', header: '5t', label: '5T' },
    Toddler6: { name: 'Toddler6', key: 'toddler6', header: '6t', label: '6T' },
    Toddler7: { name: 'Toddler7', key: 'toddler7', header: '7t', label: '7T' },
    OneSize: { name: 'OneSize', key: 'oneSize', header: 'os', label: 'OneSize' },
};

/** Default Major Accounts columns when no TSB_Sizes config is returned. */
export const DEFAULT_ADULT_SIZE_NAMES = ['Small', 'Medium', 'Large', 'XLarge', 'XXL', 'XXXL'];

/** Default Campus columns (adult + youth) when no TSB_Sizes config is returned. */
export const DEFAULT_CAMPUS_SIZE_NAMES = [
    'Small', 'Medium', 'Large', 'XLarge', 'XXL', 'XXXL',
    'YXSmall', 'YSmall', 'YMedium', 'YLarge', 'YXLarge',
];

export function resolveRosterSizeColumns(
    activeAdultSizes?: string[] | null,
    activeYouthSizes?: string[] | null,
    isOneSize?: boolean,
    fallbackNames: string[] = DEFAULT_ADULT_SIZE_NAMES
): RosterSizeColumn[] {
    if (isOneSize) {
        return [SIZE_DEFS.OneSize];
    }

    const names = [
        ...(activeAdultSizes || []),
        ...(activeYouthSizes || []),
    ].filter((name, index, all) => name && all.indexOf(name) === index);

    const source = names.length ? names : fallbackNames;
    return source
        .map(name => SIZE_DEFS[name])
        .filter((col): col is RosterSizeColumn => !!col);
}

export function sumRosterSize(roster: any[], key: string): number {
    let total = 0;
    for (const row of roster || []) {
        const value = row && row[key];
        total += typeof value === 'number' ? value : (parseInt(value, 10) || 0);
    }
    return total;
}

export function getRosterSizeTotals(roster: any[], columns: RosterSizeColumn[]): { Total: number } & { [label: string]: number } {
    const totals: { Total: number } & { [label: string]: number } = { Total: 0 };
    for (const col of columns) {
        const count = sumRosterSize(roster, col.key);
        totals[col.label] = count;
        totals.Total += count;
    }
    return totals;
}
